// Restaurant home: every store with a health snapshot — its trading state,
// its channels and today's numbers (the store's own sales summary) — each
// linking into the store's workspace. Creating a store is a provisioning
// act (super only), so the form appears for platform staff alone.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Plus, Store as StoreIcon } from 'lucide-react';
import { api, money, type Me, type Report } from '../../lib/api';
import { AU_TIMEZONES, storeState, type Restaurant, type Store, type StoreCreate } from './types';

type Today = { orders: number; revenue: number } | 'unavailable';

function Channel(props: { label: string; on: boolean }) {
  return (
    <span className="chip" data-channel={props.label} data-on={props.on}
      style={{ '--dot': props.on ? 'var(--ok)' : 'var(--ink-3)' } as React.CSSProperties}>
      {props.label}
    </span>
  );
}

const EMPTY: StoreCreate = { name: '', address: '', phoneE164: '' };

export function Stores() {
  const { rid = '' } = useParams();
  const [stores, setStores] = useState<Store[] | null>(null);
  const [today, setToday] = useState<Record<string, Today>>({});
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [isSuper, setIsSuper] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<StoreCreate>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.get<Store[]>(`/restaurants/${rid}/stores`);
      setStores(list);
      setError(null);
      const figures = await Promise.all(list.map(async (s): Promise<[string, Today]> => {
        try {
          const r = await api.get<Report>(`/restaurants/${rid}/stores/${s.storeId}/reports/sales/summary`, { preset: 'today' });
          return [s.storeId, { orders: Number(r.totals?.orders ?? 0), revenue: Number(r.totals?.revenue ?? 0) }];
        } catch {
          return [s.storeId, 'unavailable'];
        }
      }));
      setToday(Object.fromEntries(figures));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load.');
    }
  }, [rid]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    api.get<Me>('/me').then((me) => setIsSuper(me.isSuperAdmin)).catch(() => setIsSuper(false));
    api.get<Restaurant>(`/restaurants/${rid}`).then(setRestaurant).catch(() => setRestaurant(null));
  }, [rid]);

  function open() {
    setForm({ ...EMPTY, timezone: restaurant?.timezone ?? 'Australia/Sydney' });
    setFormError(null);
    setCreating(true);
  }

  async function create() {
    setSaving(true);
    setFormError(null);
    try {
      await api.post<Store>(`/restaurants/${rid}/stores`, {
        name: form.name.trim(), address: form.address.trim(), phoneE164: form.phoneE164.trim(), timezone: form.timezone,
      });
      setCreating(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to create the store.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Restaurant{restaurant ? ` · ${restaurant.name}` : ''}</div>
          <h1>Stores</h1>
        </div>
        {isSuper && (
          <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={() => (creating ? setCreating(false) : open())}>
            <Plus size={14} /> New store
          </button>
        )}
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      {creating && (
        <div className="panel" style={{ marginBottom: 20, maxWidth: 560 }} data-section="new-store">
          <h2>New store</h2>
          {formError && <div className="err" role="alert">{formError}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
            <div className="field">
              <label htmlFor="store-name">Store name</label>
              <input id="store-name" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="store-address">Address</label>
              <input id="store-address" className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label htmlFor="store-phone">Phone (E.164)</label>
                <input id="store-phone" className="input" placeholder="+61240000000" value={form.phoneE164}
                  onChange={(e) => setForm({ ...form, phoneE164: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="store-tz">Timezone</label>
                <select id="store-tz" className="input" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
                  {AU_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
            </div>
            <div>
              <button className="btn btn-primary btn-sm"
                disabled={saving || !form.name.trim() || !form.address.trim() || !form.phoneE164.trim()}
                onClick={() => void create()}>
                {saving ? 'Creating…' : 'Create store'}
              </button>
            </div>
          </div>
        </div>
      )}

      {stores === null && !error && <div className="loading">Loading…</div>}
      {stores?.length === 0 && <div className="loading">No stores yet.</div>}
      <div className="sub-list" style={{ maxWidth: 'none' }} data-section="stores">
        {stores?.map((s) => {
          const st = storeState(s);
          const t = today[s.storeId];
          return (
            <Link key={s.storeId} className="sub-card" to={`/r/${rid}/s/${s.storeId}`} data-store={s.storeId}>
              <b style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StoreIcon size={14} /> {s.name}
                <span className="chip" data-state={st.state} style={{ '--dot': st.dot } as React.CSSProperties}>{st.label}</span>
              </b>
              <span>{s.address} · {s.phoneE164} · {s.timezone}</span>
              <span style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <Channel label="Pickup" on={s.pickupEnabled} />
                <Channel label="Delivery" on={s.deliveryEnabled} />
                <Channel label="Dine-in" on={s.dineInEnabled} />
              </span>
              <span style={{ display: 'block', marginTop: 8, color: 'var(--ink-2)' }}>
                Today:{' '}
                {t === undefined ? '…' : t === 'unavailable' ? 'figures unavailable' : (
                  <>
                    {/* strong, not b: the card styles its b as the title block */}
                    <strong data-today="orders">{t.orders}</strong> {t.orders === 1 ? 'order' : 'orders'} ·{' '}
                    <strong data-today="revenue">{money(t.revenue)}</strong>
                  </>
                )}
              </span>
            </Link>
          );
        })}
      </div>
    </>
  );
}
